import type { FieldProps } from 'formik';
import { Field, Form, Formik } from 'formik';
import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { object, string } from 'yup';

import ContentLoader from '@components/ContentLoader';
import MainTitle from '@components/MainTitle';
import RoundedButton from '@components/RoundedButton/index';
import Textarea from '@components/Textarea';

import { RECORD_UPDATE_TYPES } from '@constants/record-update-types';

import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import { useSendRecordReportedEmailLazyQuery } from 'generated/feedback.generated';

import { RecordsUpdateStyle } from './ReportContent.style';

const messages = defineMessages({
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
  postError: {
    id: 'reportContent.postError',
    defaultMessage: 'An error occurred',
  },
  postSuccess: {
    id: 'reportContent.postSuccess',
    defaultMessage: 'Reported',
  },
  encounterCopy: {
    id: 'reportContent.encounterCopy',
    defaultMessage:
      'If you have multiple encounters to report, please include all of them in the form below.',
  },
  incorrectCopy: {
    id: 'reportContent.incorrectCopy',
    defaultMessage:
      'If there are multiple issues to report, please include all of them in the form below.',
  },
  missingCopy: {
    id: 'reportContent.missingCopy',
    defaultMessage:
      'If there are multiple items missing, please include all of them in the form below.',
  },
  updateMessage: {
    id: 'reportContent.updateMessage',
    defaultMessage:
      'Please provide some details about the record. For example, the location or clinic, physician or provider name, date, etc.',
  },
  incorrectMessage: {
    id: 'reportContent.incorrectMessage',
    defaultMessage:
      "Fixing misinformation is very important to us. Tell us what isn't right and we'll take it from there.",
  },
  missingMessage: {
    id: 'reportContent.missingMessage',
    defaultMessage:
      "Please provide a few details to help us fill in what is missing and we'll take it from there.",
  },
  updateTitle: {
    id: 'reportContent.updateTitle',
    defaultMessage: 'Tell us about an encounter',
  },
  missingTitle: {
    id: 'reportContent.missingTitle',
    defaultMessage: 'Is something missing from your records?',
  },
  incorrectTitle: {
    id: 'reportContent.incorrectTitle',
    defaultMessage: 'Is something not right?',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
});

const ReportContent: React.FC<{
  /**
   * Callback for when the submit button is clicked
   */
  onSubmit?: () => void;

  type: RECORD_UPDATE_TYPES;
}> = ({ onSubmit = () => null, type = RECORD_UPDATE_TYPES.RECORD_UPDATE }) => {
  const { formatMessage } = useIntl();

  const {
    state: { activeProfile },
  } = useSession();
  const [user_id] = [activeProfile?.user_id, activeProfile?.account_id];
  const { enqueueSnackbar } = useSnackbar();

  const [sendRecordReportedEmail, { loading }] =
    useSendRecordReportedEmailLazyQuery();

  let title = '';
  let subtitle = '';
  let copy = '';
  switch (type) {
    case RECORD_UPDATE_TYPES.INCORRECT_INFORMATION:
      title = formatMessage(messages.incorrectTitle);
      subtitle = formatMessage(messages.incorrectMessage);
      copy = formatMessage(messages.incorrectCopy);
      break;
    case RECORD_UPDATE_TYPES.MISSING_INFORMATION:
      title = formatMessage(messages.missingTitle);
      subtitle = formatMessage(messages.missingMessage);
      copy = formatMessage(messages.missingCopy);
      break;
    case RECORD_UPDATE_TYPES.RECORD_UPDATE:
    default:
      title = formatMessage(messages.updateTitle);
      subtitle = formatMessage(messages.updateMessage);
      copy = formatMessage(messages.encounterCopy);
      break;
  }

  const initialValues: {
    report: string;
    submit: string | null;
  } = {
    report: '',
    submit: null,
  };

  const validationSchema = object().shape({
    report: string().required(formatMessage(messages.required)),
  });

  return (
    <RecordsUpdateStyle>
      <MainTitle title={title} />
      <p>{subtitle}</p>
      <p>{copy}</p>
      <ContentLoader lineCount={1} loading={loading}>
        <Formik
          initialValues={initialValues}
          onSubmit={async (values, { setSubmitting }) => {
            if (!activeProfile?.patient_id) {
              return;
            }

            let message = '';
            let emailType = RECORD_UPDATE_TYPES.RECORD_UPDATE;
            switch (type) {
              case RECORD_UPDATE_TYPES.INCORRECT_INFORMATION:
                message = 'has reported incorrect information on a record.';
                emailType = RECORD_UPDATE_TYPES.INCORRECT_INFORMATION;
                break;
              case RECORD_UPDATE_TYPES.MISSING_INFORMATION:
                message = 'has reported missing information on a record.';
                emailType = RECORD_UPDATE_TYPES.MISSING_INFORMATION;
                break;
              case RECORD_UPDATE_TYPES.RECORD_UPDATE:
              default:
                message = 'has reported a new record.';
                emailType = RECORD_UPDATE_TYPES.RECORD_UPDATE;
                break;
            }

            try {
              const emailSent = await sendRecordReportedEmail({
                variables: {
                  input: {
                    user_id: user_id ?? '',
                    message,
                    type: emailType,
                    taskContent: values.report,
                  },
                },
              });

              if (emailSent.data?.sendRecordReportedEmail) {
                enqueueSnackbar({
                  variant: 'success',
                  message: formatMessage(messages.postSuccess),
                });
              }
              onSubmit();
            } catch (e) {
              enqueueSnackbar({
                variant: 'error',
                message: formatMessage(messages.postError),
              });
            } finally {
              setSubmitting(false);
            }
          }}
          validationSchema={validationSchema}
        >
          {({ errors, isSubmitting }) => (
            <Form>
              <Field name="report">
                {({ field, meta }: FieldProps<string>) => (
                  <Textarea
                    message={meta.touched ? errors.report : ''}
                    {...field}
                  />
                )}
              </Field>
              <RoundedButton
                disabled={isSubmitting || loading}
                margin={10}
                type="submit"
                width="150px"
              >
                {formatMessage(messages.submit)}
              </RoundedButton>
            </Form>
          )}
        </Formik>
      </ContentLoader>
    </RecordsUpdateStyle>
  );
};

export default ReportContent;
